import { IOgqlV1Column } from '@opengov/ogql-types';
import { generateUniqueRowKey } from '../../utils/uniqueKeyUtils';
import {
  ColumnDefinition,
  ColumnValue,
  ColumnValueSegment,
  getMultiHierarchyFirstRootRowId,
  getRowGroupName,
  IAccountSummary,
  IColumnSource,
  IRowSource,
  isCalculationExpression,
  isMultiHierarchyCalculatedRowGroup,
  isMultiHierarchyRowGroup,
  MultiHierarchyRowGroup,
  TOTALS_ROW_LABEL_SUFFIX
} from '@opengov/reporting-types';
import isNil from 'lodash/isNil';
import { buildMultiHierarchyParentRowId } from '../editor/sidepanel/rowEditor/multigroupingUtils';
import { filterZerosInChildRows, filterZerosInParentRows } from './getRowDataUtils';

export type RowMetadata = {
  hasChildren: boolean;
  label: string;
  isBreakdownRow?: boolean;
  /**
   * True when this row is the synthesized aggregate row for a non-CoA breakdown (e.g.
   * "proposal_status Totals" under an expanded non-CoA parent). CoA totals rows come
   * through the standard SQL path and do not set this flag; the frontend uses it to
   * apply a distinct background color from CoA totals rows.
   */
  isNonCoaTotalsRow?: boolean;
  /**
   * Absolute config breakdown level of a breakdown row (1 = first level below the root, 2 = second,
   * ...). Set by the backend for breakdown rows; used to indent each root-level breakdown row by
   * its depth so a multi-level breakdown steps in once per level.
   */
  breakdownLevel?: number;
  /**
   * True when this (deepest-level group leaf) row expands to reveal leaf detail rows rather than
   * child groups: account-number rows when an Account Number level is configured, otherwise the
   * group's segment-code rows. The datasource reads it to send `expandLeafDetail` when fetching
   * this row's children.
   */
  expandsToLeafDetail?: boolean;
  /**
   * True for an account or segment-code row — its own grain, never a group. Set on the codes or
   * accounts listed in a hidden group row's place (BUD-19598), which a root-level breakdown shows
   * with the figures they arrived with and never drills.
   */
  isLeafDetailRow?: boolean;
};

export type RowsMetadata = Record<string, RowMetadata>;

function lookupRowMetadata(
  groupId: string | number | null | undefined,
  rowsMetadata?: RowsMetadata
): RowMetadata | undefined {
  if (groupId == null || rowsMetadata == null) {
    return undefined;
  }
  return rowsMetadata[String(groupId)];
}

export type TransformParentRowsToAgGridArgs = {
  /** Index-aligned with `rowGroups`; an `undefined` entry is a parent response still in flight. */
  parentRows: (ColumnValueSegment[] | undefined)[] | undefined;
  rowGroups: IRowSource[];
  metadataColumnsPerRow: IOgqlV1Column[][];
  suppressZerosInRows: boolean;
  formatting: IAccountSummary['formatting'];
  columnGroups: IColumnSource[];
  columnDefinitions: ColumnDefinition[];
  rowsMetadata?: RowsMetadata;
  /** Multi-hierarchy only: when false, parent rows show values when expanded (no totals row). Default true. */
  showTotalsRows?: boolean;
};

export type TransformChildRowsToAgGridArgs = {
  childRows: ColumnValue[];
  rowGroup: IRowSource;
  rowGroupIndex: number;
  nodeLevel: number;
  metadataColumns: IOgqlV1Column[];
  suppressZerosInRows: boolean;
  formatting: IAccountSummary['formatting'];
  columnGroups: IColumnSource[];
  columnDefinitions: ColumnDefinition[];
  parentRowGroupId?: string;
  parentRowKey?: string;
  rowsMetadata?: RowsMetadata;
  totalRowIndex?: number;
  /** Multi-hierarchy only: when false, expandable rows are not blanked when expanded. Default true. */
  showTotalsRows?: boolean;
  /**
   * Multi-hierarchy only: true when these rows are the children of a top-level row group row, as
   * opposed to a nested group or an injected root-level breakdown row. Only then does the totals
   * row aggregate the row group itself and take the name the user gave it.
   */
  isRootRowExpand?: boolean;
  /** When set, breakdown rows use this groupId as the symbiote instead of scanning backward. */
  symbioteGroupIdOverride?: string;
  /**
   * Extra indent steps inherited from an injected root-level breakdown ancestor. Every row fetched
   * beneath an injected row carries the ancestor's value unchanged: the row's own ag-grid level
   * already counts its depth below that ancestor.
   */
  breakdownIndentLevels?: number;
};

export type TransformRootBreakdownRowsToAgGridArgs = {
  rootBreakdownRows: ColumnValueSegment[][];
  rowGroup: IRowSource;
  rowGroupIndex: number;
  rootGroupId: string;
  parentRowId: string;
  parentRowKey: string;
  metadataColumns: IOgqlV1Column[];
  suppressZerosInRows: boolean;
  formatting: IAccountSummary['formatting'];
  columnGroups: IColumnSource[];
  columnDefinitions: ColumnDefinition[];
  rowsMetadata?: RowsMetadata;
  /** When true (default), expandable injected rows blank on expand like other MH parents. */
  showTotalsRows?: boolean;
};

/**
 * The row group's own `displayName`, for the root row `groupId` names. On a plain row group with
 * several roots it belongs to the first root row only — where the report reducers keep it — so the
 * other roots fall back to their own labels instead of repeating it. Reports saved before that rule
 * still carry it at row level, and this keeps them from rendering one name down every root. An ERP
 * override row is named as a whole, so every root row keeps its name.
 */
function rowLevelNameFor(rowGroup: IRowSource, groupId: ColumnValueSegment | undefined) {
  const { displayName } = rowGroup as MultiHierarchyRowGroup;
  if (!isMultiHierarchyRowGroup(rowGroup)) {
    return displayName;
  }
  const firstRootRowId = getMultiHierarchyFirstRootRowId(rowGroup);
  const isAnotherRoot = firstRootRowId !== undefined && String(groupId) !== firstRootRowId;
  return isAnotherRoot ? undefined : displayName;
}

/**
 * The name the user gave a multi-hierarchy row: a per-group name when that group was renamed on its
 * own, otherwise the row group's display name. Returns undefined when neither is set, leaving
 * callers on the label the backend derived from the chart of accounts.
 *
 * An empty name counts as unset — a row group can carry `displayName: ''` and must still fall back
 * rather than render a blank label, so the emptiness check cannot be a `??`.
 */
function resolveConfiguredRowLabel(rowGroup: IRowSource, groupId: ColumnValueSegment | undefined) {
  const mhRowGroup = rowGroup as MultiHierarchyRowGroup;
  const perGroupName = groupId != null ? mhRowGroup.groupDisplayNames?.[String(groupId)] : undefined;
  const configuredName = perGroupName ?? rowLevelNameFor(rowGroup, groupId);
  if (!configuredName) {
    return undefined;
  }
  return configuredName;
}

/**
 * A child row's label. Rows read the label the backend derived from the chart of accounts, except
 * the totals row of a top-level row: that one aggregates the row group itself, so it takes the name
 * the user gave it. The backend labels every totals row from the chart of accounts, which would
 * otherwise leave it reading "<COA group> Totals" beneath a parent row showing the configured name.
 */
function resolveChildRowLabel({
  rowGroup,
  groupId,
  rowMeta,
  isConfiguredTotalsRow
}: {
  rowGroup: IRowSource;
  groupId: ColumnValueSegment | undefined;
  rowMeta: RowMetadata | undefined;
  isConfiguredTotalsRow: boolean;
}) {
  const configuredLabel = isConfiguredTotalsRow ? resolveConfiguredRowLabel(rowGroup, groupId) : undefined;
  if (configuredLabel) {
    return `${configuredLabel} ${TOTALS_ROW_LABEL_SUFFIX}`;
  }
  return rowMeta ? rowMeta.label : groupId;
}

export function transformParentRowsToAgGrid({
  parentRows,
  rowGroups,
  metadataColumnsPerRow,
  suppressZerosInRows,
  formatting,
  columnGroups,
  columnDefinitions,
  rowsMetadata,
  showTotalsRows
}: TransformParentRowsToAgGridArgs) {
  if (!parentRows) return [];
  const isMultiHierarchy = !isNil(rowsMetadata);
  const transformedParentRows = rowGroups.map((rowGroup, index) => {
    const rawRow = parentRows[index];
    const groupId = rawRow?.[0];
    const rowMeta = lookupRowMetadata(groupId, rowsMetadata);
    const rawValues = rawRow?.slice(1) ?? [];
    const values = rawValues;

    const configuredLabel = isMultiHierarchy ? resolveConfiguredRowLabel(rowGroup, groupId) : undefined;
    const fallbackLabel = rowMeta ? rowMeta.label : getRowGroupName(rowGroup);
    const label = configuredLabel ?? fallbackLabel;
    const isCalculation = isCalculationExpression(rowGroup) || isMultiHierarchyCalculatedRowGroup(rowGroup);

    const canExpand = rowMeta ? rowMeta.hasChildren : !isCalculation && rowGroup?.grouping?.length > 0;

    // Use <rowGroupId>:<groupId> format for multi-hierarchy so rows from different
    // groupIds within the same rowGroup remain distinct in ag-grid.
    const id =
      isMultiHierarchy && rowGroup.id && groupId
        ? buildMultiHierarchyParentRowId(rowGroup.id, String(groupId))
        : rowGroup.id;

    return {
      id,
      rowGroupIndex: index,
      row_group_0: label,
      values: values,
      // The progressive parent load hands this transform an undefined entry for a row group whose
      // response has not arrived. The flag makes the value cells render as loading blocks (see
      // PendingValueCell), which also keeps the account-summary-load gauge waiting until every
      // parent's values are actually on screen.
      ...(rawRow === undefined && { pendingValues: true }),
      canExpand: canExpand,
      // hasTotalRow: Tells the tableFormatter to blank expanded row values when a
      // dedicated totals child row is visible. Only true when showTotalsRows is enabled,
      // so that parent values are shown directly when totals rows are hidden.
      ...(isMultiHierarchy && {
        hasTotalRow: showTotalsRows !== false,
        isTopLevel: true,
        groupId,
        expandsToLeafDetail: Boolean(rowMeta?.expandsToLeafDetail)
      }),
      groupData: {
        type: rowGroup.type,
        metadataColumns: metadataColumnsPerRow[index]
      }
    };
  });

  return suppressZerosInRows
    ? filterZerosInParentRows(transformedParentRows, metadataColumnsPerRow, formatting, columnGroups, columnDefinitions)
    : transformedParentRows;
}

export function transformChildRowsToAgGrid({
  childRows,
  rowGroup,
  rowGroupIndex,
  nodeLevel,
  metadataColumns = [],
  suppressZerosInRows,
  formatting,
  columnGroups,
  columnDefinitions,
  parentRowGroupId,
  parentRowKey,
  rowsMetadata,
  totalRowIndex,
  showTotalsRows,
  symbioteGroupIdOverride,
  breakdownIndentLevels,
  isRootRowExpand
}: TransformChildRowsToAgGridArgs) {
  if (!childRows) return [];
  const isMultiHierarchy = !isNil(rowsMetadata);
  const totalsRowsVisible = showTotalsRows !== false;
  const filteredChildRows = suppressZerosInRows
    ? filterZerosInChildRows(childRows, metadataColumns, formatting, columnGroups, columnDefinitions)
    : childRows;

  const transformed = filteredChildRows.map((row, index) => {
    const groupId = row?.[0];
    const rowMeta = lookupRowMetadata(groupId, rowsMetadata);
    const isCalculation = isCalculationExpression(rowGroup) || isMultiHierarchyCalculatedRowGroup(rowGroup);
    const canExpand = rowMeta ? rowMeta.hasChildren : !isCalculation && nodeLevel < rowGroup.grouping.length;
    const isTotalRow = typeof totalRowIndex === 'number' && index === totalRowIndex;
    const isConfiguredTotalsRow = Boolean(isMultiHierarchy && isRootRowExpand && isTotalRow);
    const displayLabel = resolveChildRowLabel({ rowGroup, groupId, rowMeta, isConfiguredTotalsRow });
    const expandsToLeafDetailFlag = Boolean(rowMeta?.expandsToLeafDetail);
    const uniqueRowKey = generateUniqueRowKey(parentRowGroupId ?? rowGroup.id, index, row.slice(1));

    const isBreakdownRowFlag = Boolean(rowsMetadata?.[String(groupId)]?.isBreakdownRow);
    const isNonCoaTotalRowFlag = Boolean(rowsMetadata?.[String(groupId)]?.isNonCoaTotalsRow);

    // For breakdown rows, build a compound key from the nearest preceding non-breakdown row's groupId
    // (the "symbiote") so all Level-2 breakdown rows under the same Level-1 parent share the same
    // symbiote. Scanning backward skips over sibling breakdown rows when there are multiple Level-2 groups.
    const findNonBreakdownSymbiote = (): string | undefined => {
      for (let j = index - 1; j >= 0; j--) {
        const prevGroupId = String(filteredChildRows[j]?.[0] ?? '');
        if (!rowsMetadata?.[prevGroupId]?.isBreakdownRow) return prevGroupId;
      }
      return undefined;
    };
    const symbioteFromScan =
      isMultiHierarchy && isBreakdownRowFlag && index > 0 ? findNonBreakdownSymbiote() : undefined;
    const symbioteGroupId =
      isMultiHierarchy && isBreakdownRowFlag ? (symbioteGroupIdOverride ?? symbioteFromScan) : undefined;
    const nodeKey = symbioteGroupId ? `${symbioteGroupId}:${String(groupId)}` : undefined;
    const breakdownLevel = isMultiHierarchy && isBreakdownRowFlag ? 1 : undefined;

    const rowData = {
      id: `${String(parentRowGroupId ?? rowGroup.id)}.${String(row[0])}-key-${uniqueRowKey}`,
      rowKey: `${String(parentRowKey ?? rowGroup.id)}.${String(row[0])}`,
      rowGroupIndex,
      row_group_0: getRowGroupName(rowGroup),
      [`row_group_${String(nodeLevel)}`]: displayLabel,
      values: row.slice(1),
      canExpand,
      // hasTotalRow: Tells the tableFormatter to blank expanded row values when a
      // dedicated totals child row is visible. Only set for expandable children and only
      // when totals rows are enabled, so values are shown directly when totals rows are hidden.
      ...(isMultiHierarchy && {
        hasTotalRow: canExpand && totalsRowsVisible,
        isTotalRow,
        groupId, // unchanged: used for data fetching
        nodeKey, // used by createNodePath for expandedRows key uniqueness among sibling breakdown rows
        isBreakdownRow: isBreakdownRowFlag,
        isNonCoaTotalRow: isNonCoaTotalRowFlag,
        ...(breakdownLevel != null && { breakdownLevel }),
        ...(breakdownIndentLevels ? { breakdownIndentLevels } : {}),
        expandsToLeafDetail: expandsToLeafDetailFlag
      }),
      groupData: {
        type: rowGroup.type,
        metadataColumns
      }
    };

    return rowData;
  });

  if (!totalsRowsVisible) {
    return transformed.filter((row) => !row.isTotalRow);
  }
  return transformed;
}

/**
 * Transforms first-level breakdown rows from the parent query for collapsed-root SSRM injection (BUD-16094).
 * Rows are added at level 0 via applyServerSideTransaction while the root is collapsed only.
 */
export function transformRootBreakdownRowsToAgGrid({
  rootBreakdownRows,
  rowGroup,
  rowGroupIndex,
  rootGroupId,
  parentRowId,
  parentRowKey,
  metadataColumns,
  suppressZerosInRows,
  formatting,
  columnGroups,
  columnDefinitions,
  rowsMetadata,
  showTotalsRows
}: TransformRootBreakdownRowsToAgGridArgs) {
  if (!rootBreakdownRows.length || isNil(rowsMetadata)) {
    return [];
  }

  const childRows = rootBreakdownRows as ColumnValue[];
  const transformed = transformChildRowsToAgGrid({
    childRows,
    rowGroup,
    rowGroupIndex,
    nodeLevel: 1,
    metadataColumns,
    suppressZerosInRows,
    formatting,
    columnGroups,
    columnDefinitions,
    parentRowGroupId: parentRowId,
    parentRowKey,
    rowsMetadata,
    symbioteGroupIdOverride: rootGroupId,
    showTotalsRows: false
  });

  return transformed.map((row) => {
    const groupId = row.groupId;
    const rowMeta = groupId != null ? rowsMetadata[String(groupId)] : undefined;
    let breakdownLabel = row.row_group_0;
    if (groupId != null) {
      breakdownLabel = rowMeta ? rowMeta.label : String(groupId);
    }
    const canExpand = rowMeta?.hasChildren ?? row.canExpand;
    const isLeafDetailRow = rowMeta?.isLeafDetailRow === true;
    const totalsRowsVisible = showTotalsRows !== false;
    const indentLevels = rowMeta?.breakdownLevel ?? 1;
    return {
      ...row,
      row_group_0: breakdownLabel,
      isRootLevelBreakdown: true,
      breakdownLevel: indentLevels,
      // Injected rows all sit at ag-grid level 0, so their configured depth is expressed as an
      // indent offset instead. Everything fetched beneath one inherits this value unchanged, which
      // keeps the whole subtree on the indent columns it will occupy once the root is expanded.
      breakdownIndentLevels: indentLevels,
      canExpand,
      isBreakdownRow: true,
      isLeafDetailRow,
      hasTotalRow: canExpand && totalsRowsVisible,
      // Parent-query breakdown values reflect the root aggregate, not the per-breakdown total.
      // Clear them so the row shows blanks until syncBreakdownRowValuesFromChildTotals
      // populates accurate values from the child-fetch totals row on first expand — a childless
      // GROUP row included, whose drill answers with its own totals. A code or account listed in a
      // hidden group row's place (BUD-19598) is its own grain: its values are already right.
      // Typed empty array keeps the inferred `values` type identical to the spread row's
      // (ColumnValueSegment[]) so downstream type-narrowing/lint behavior is unchanged.
      values: isLeafDetailRow ? row.values : ([] as ColumnValueSegment[])
    };
  });
}
